import { useMemo } from 'react';

import {
  typographyStyle,
  Flex,
  Dropdown,
  Button,
  IconDownloadFileFill,
  Colors,
  Text,
} from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { Divider, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import {
  DownloadDataType,
  PurchaseInvoiceMappingExcelExportOptions,
  SalesInvoiceMappingExcelExportOptions,
} from '~apis/download';
import GuideButton from '~components/ui/Button/GuideButton';
import DateRangePickerWithPreset from '~components/ui/DatePicker/DateRangePickerWithPreset';
import { SETTLEMENT_VS_GUIDE_LINK } from '~constants/guide';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { TaxInvoiceType } from '~models/tax-invoice';

import SettlementListFilter from './SettlementListFilter';
import { SettlementStatus } from './utils';
import useExportExcel from '../../hooks/download/useExportExcel';

interface Props {
  type: TaxInvoiceType;
  rangeDateValue: [Dayjs, Dayjs];
  setRangeDateValue: (value: [Dayjs, Dayjs]) => void;
  onSearch: (value: string) => void;
  searchParam?: string;
  activeStatus?: SettlementStatus;
  setStatus: (activeStatus?: SettlementStatus) => void;
  sort?: string;
}

const SettlementListHeader: React.FC<Props> = ({
  type,
  rangeDateValue,
  setRangeDateValue,
  onSearch,
  activeStatus,
  setStatus,
  searchParam,
  sort,
}) => {
  const { companyId } = useDemoAwareCompanyId();

  const disabledDate = (current: Dayjs) => {
    // 현재달보다 이후는 선택 불가능
    return current > dayjs().endOf('month');
  };

  const { dropdownItems, dropdownMenuStyle } = useExportExcel({
    dataType: `${type}_INVOICE_MAPPING` as DownloadDataType,
  });
  const items = useMemo(() => {
    const options =
      type === 'SALES'
        ? {
            salesInvoiceMappingOptions: {
              statusFilter: activeStatus,
              searchParamFilter: searchParam,
            } satisfies SalesInvoiceMappingExcelExportOptions,
          }
        : {
            purchaseInvoiceMappingOptions: {
              statusFilter: activeStatus,
              searchParamFilter: searchParam,
            } satisfies PurchaseInvoiceMappingExcelExportOptions,
          };
    return dropdownItems?.({
      companyId,
      endDate: rangeDateValue[1],
      rangeType: 'month',
      startDate: rangeDateValue[0],
      ...options,
      sort,
    });
  }, [type, activeStatus, searchParam, dropdownItems, companyId, rangeDateValue, sort]);

  return (
    <Flex
      direction="column"
      gap={16}
      style={{
        width: '100%',
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={12}>
          <Typography.Title
            style={{
              ...typographyStyle['16_600'],
              margin: 0,
            }}
          >
            {`${type === 'SALES' ? '매출' : '매입'} 정산`}
          </Typography.Title>
          <GuideButton link={SETTLEMENT_VS_GUIDE_LINK} />
          <Divider
            type="vertical"
            className={css`
              height: 20px;
              border-inline-color: ${colorTokens.borderSubtle};
            `}
          />
          <DateRangePickerWithPreset
            showType={false}
            rangeType={'month'}
            value={rangeDateValue}
            onDateRangeCompleted={(tempChangedValues) => {
              setRangeDateValue([
                tempChangedValues[0]?.startOf('month'),
                tempChangedValues[1]?.endOf('month'),
              ] as [Dayjs, Dayjs]);
            }}
            datePickerProps={{
              bordered: false,
              allowClear: false,
              allowEmpty: [false, false],
              disabledDate,
            }}
          />
        </Flex>
        <Flex gap={8}>
          <Dropdown
            disableScroll
            menu={{
              items,
              style: {
                width: 196,
                padding: 12,
              },
              className: dropdownMenuStyle,
            }}
          >
            <Button
              border
              icon={<IconDownloadFileFill width={18} height={18} fill={Colors.green500} />}
            >
              <Text textStyle="14_400">엑셀 다운로드</Text>
            </Button>
          </Dropdown>
        </Flex>
      </Flex>
      <SettlementListFilter
        onSearch={onSearch}
        activeStatus={activeStatus}
        setStatus={setStatus}
        searchParam={searchParam}
      />
    </Flex>
  );
};

export default SettlementListHeader;
